'use client';

import {
	MonitoringConfigCard,
	type MonitoringConfigCardData,
	MonitoringConfigCardSkeleton,
	type MonitoringTab,
	MonitoringView,
} from '@slims/ui/organisms';
import { StatusBlock } from '@slims/ui/molecules';
import { useMemo, useState } from 'react';
import { MetricExplode, type MetricExplodeTarget } from '@/components/energy/metric-explode';
import { PodExplode, type PodExplodeTarget } from '@/components/energy/pod-explode';
import { useConsumptionByMeter } from '@/hooks/use-consumption-by-meter';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useEnergyMembers } from '@/hooks/use-energy-members';
import { usePlantDetail } from '@/hooks/use-plant-detail';
import { usePlants } from '@/hooks/use-plants';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { type ConfigToggleProps, useConfigToggle } from '@/hooks/use-config-toggle';
import { useHasRole } from '@/hooks/use-permission';

/** Intervallo `from/to` del mese scelto (o del corrente) per i consumi per misuratore. */
function meterRangeFor(period: string | null): { from: string; to: string } {
	const base = period ? new Date(`${period}-01T00:00:00Z`) : new Date();
	const year = base.getUTCFullYear();
	const monthIdx = base.getUTCMonth();
	return {
		from: new Date(Date.UTC(year, monthIdx, 1)).toISOString(),
		to: new Date(Date.UTC(year, monthIdx + 1, 1)).toISOString(),
	};
}

/**
 * Come la Dashboard, il Monitoraggio guarda le configurazioni scelte nel
 * selettore CER/configurazioni dell'header. Con più di una in ambito compare il
 * toggle «Panoramica»/«Configurazione singola»: la Panoramica è una card di
 * portfolio per configurazione, il clic apre il monitoraggio completo di quella.
 */
export default function MonitoringPage() {
	const { scopes, loading: scopesLoading, effectiveLeaf, openConfig, toggle, showOverview } =
		useConfigToggle();
	// La scheda scelta resta aperta anche se il dettaglio viene rimontato:
	// cambio configurazione e passaggio Panoramica↔singola non sono cambi di sezione.
	const [tab, setTab] = useState<MonitoringTab>('produzione');
	// Il mese guardato vive qui per restare invariato passando Panoramica↔singola.
	const [period, setPeriod] = useState<string | null>(null);

	if (!effectiveLeaf) {
		return (
			<StatusBlock
				kind={scopesLoading ? 'loading' : 'empty'}
				size="lg"
				message={
					scopesLoading
						? 'Caricamento...'
						: 'Nessuna configurazione selezionata. Scegli una vista dal selettore in alto.'
				}
			/>
		);
	}

	if (showOverview) {
		return (
			<MonitoringView
				{...toggle}
				tab={tab}
				onTabChange={setTab}
				energyTotals={{ totalImmessa: 0, totalPrelevata: 0, totalCondivisa: 0 }}
				period={period}
				onPeriodChange={setPeriod}
				overviewGridSlot={scopes.map((leaf) => (
					<MonitoringConfigCardWiring
						key={leaf.orgId}
						leaf={leaf}
						tab={tab}
						month={period ?? undefined}
						onOpen={() => openConfig(leaf.orgId)}
					/>
				))}
			/>
		);
	}

	return (
		<MonitoringSingleWiring
			key={effectiveLeaf.orgId}
			leaf={effectiveLeaf}
			tab={tab}
			onTabChange={setTab}
			period={period}
			onPeriodChange={setPeriod}
			toggle={toggle}
		/>
	);
}

/** Alimenta una card della «Panoramica»: le grandezze di portfolio del periodo, per una configurazione sola. */
function MonitoringConfigCardWiring({
	leaf,
	tab,
	month,
	onOpen,
}: {
	leaf: ScopeLeaf;
	tab: MonitoringTab;
	month: string | undefined;
	onOpen: () => void;
}) {
	// Arricchisce la card con i dati della configurazione autorizzata.
	const { config } = useScopedConfig(leaf.orgId);
	const { portfolio, isLoading } = usePlants(leaf.orgId, month);
	const consumptionOrgId = tab === 'consumi' ? leaf.orgId : null;
	const { sharedEnergy, isLoading: energyLoading } = useEnergyData(undefined, consumptionOrgId);
	const meterRange = useMemo(() => meterRangeFor(month ?? null), [month]);
	const { meters, isLoading: metersLoading } = useConsumptionByMeter(consumptionOrgId, meterRange);
	const consumption = useMemo(() => {
		const ref = month ? new Date(`${month}-01T12:00:00`) : new Date();
		return sharedEnergy.reduce(
			(totals, point) => {
				const date = new Date(point.hour);
				if (date.getFullYear() === ref.getFullYear() && date.getMonth() === ref.getMonth()) {
					totals.periodKwh += point.totalPrelevata;
					totals.sharedKwh += point.energiaCondivisa;
				}
				return totals;
			},
			{
				periodKwh: 0,
				sharedKwh: 0,
				podCount: meters.filter((meter) => meter.kind === 'M1').length,
			},
		);
	}, [sharedEnergy, meters, month]);

	if (tab === 'consumi' ? energyLoading || metersLoading : isLoading) {
		return <MonitoringConfigCardSkeleton cerNome={leaf.cerNome} />;
	}

	const data: MonitoringConfigCardData = {
		id: leaf.orgId,
		cerNome: leaf.cerNome,
		configNome: config.nome || leaf.configNome,
		comune: leaf.comune,
		areaCode: leaf.areaConvenzionale,
		liveKw: portfolio?.liveKw ?? 0,
		prodPeriodoKwh: portfolio?.energiaPeriodoKwh ?? 0,
		impiantiOnline: portfolio?.impiantiOnline ?? 0,
		impiantiTotali: portfolio?.impiantiTotali ?? 0,
		selfConsumptionPct: portfolio?.selfConsumptionPct ?? null,
		consumption,
	};

	return <MonitoringConfigCard data={data} tab={tab} onClick={onOpen} />;
}

/**
 * Il monitoraggio completo di una configurazione sola: puro cablaggio dati. La
 * `key` sull'orgId nel chiamante rimonta questo blocco quando si cambia
 * configurazione, azzerando l'impianto selezionato (che non varrebbe per un'altra
 * configurazione).
 */
function MonitoringSingleWiring({
	leaf,
	tab,
	onTabChange,
	period,
	onPeriodChange,
	toggle,
}: {
	leaf: ScopeLeaf;
	tab: MonitoringTab;
	onTabChange: (tab: MonitoringTab) => void;
	period: string | null;
	onPeriodChange: (month: string | null) => void;
	toggle: ConfigToggleProps;
}) {
	const orgId = leaf.orgId;
	const canViewAnomalies = useHasRole('referent');
	const month = period ?? undefined;

	const { config } = useScopedConfig(orgId);
	const { members, isLoading: membersLoading } = useEnergyMembers(orgId);

	// La serie oraria si carica intera e non per periodo: il trend a 12 mesi e i
	// confronti anno su anno vivono di storico. È la vista a filtrare sul mese.
	const { sharedEnergy, totals, isLoading: energyLoading } = useEnergyData(undefined, orgId);
	const { plants, portfolio, isLoading: plantsLoading } = usePlants(orgId, month);

	const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);
	const effectivePlantId = plants.length === 1 ? plants[0].id : selectedPlantId;
	const {
		detail,
		options,
		isLoading: plantDetailLoading,
	} = usePlantDetail(orgId, effectivePlantId, month);

	// I consumi per misuratore arrivano già aggregati sul periodo, quindi la
	// finestra va chiesta al server: è l'unico dato della scheda Consumi che non
	// si ricava dalla serie oraria già in memoria.
	const meterRange = useMemo(() => meterRangeFor(period), [period]);
	const { meters: meterConsumption, isLoading: meterConsumptionLoading } = useConsumptionByMeter(
		orgId,
		meterRange,
	);

	const [metric, setMetric] = useState<MetricExplodeTarget | null>(null);
	const [pod, setPod] = useState<PodExplodeTarget | null>(null);

	return (
		<>
			<MonitoringView
				{...toggle}
				tab={tab}
				onTabChange={onTabChange}
				anomaliesEnabled={canViewAnomalies && (leaf.features?.anomalies ?? false)}
				plants={plants}
				portfolio={portfolio}
				plantOptions={options}
				plantDetail={detail}
				selectedPlantId={effectivePlantId}
				onSelectPlant={setSelectedPlantId}
				config={config}
				members={members}
				sharedEnergy={sharedEnergy}
				energyTotals={totals}
				meterConsumption={meterConsumption}
				period={period}
				onPeriodChange={onPeriodChange}
				loading={{
					production: plantsLoading,
					plantDetail: plantDetailLoading,
					consumption: energyLoading || membersLoading || meterConsumptionLoading,
				}}
				onExplodeMetric={(t) => setMetric({ ...t, orgId } as MetricExplodeTarget)}
				onExplodePod={setPod}
			/>
			<MetricExplode target={metric} onClose={() => setMetric(null)} />
			<PodExplode orgId={orgId} target={pod} onClose={() => setPod(null)} />
		</>
	);
}
